import type { HydraCollection } from "@verbleif/lib/src/composables/Api/Type/hydra.interface";
import type { UserPreferenceKey, UserPreferenceScopeType, UserPreferencesHydraItem, UserPreferenceSource, UserPreferencesWrite } from "@verbleif/lib/src/composables/Api/Type/UserPreferences/user_preferences.interface";
import { scopeTypeToApiScope } from "@verbleif/lib/src/composables/Api/Type/UserPreferences/user_preferences.interface";

export interface UserPreferenceScopeParams {
  scopeType: UserPreferenceScopeType
  scopeIri?: string | null
  clientIri?: string | null
  locationIri?: string | null
  userIri?: string | null
}

export function matchesIri(value: string | null | undefined, expected: string | null | undefined): boolean {
  if (!value || !expected) {
    return false;
  }
  if (value === expected) {
    return true;
  }
  const valueId = value.split("/").pop();
  const expectedId = expected.split("/").pop();
  return !!valueId && valueId === expectedId;
}

export function ownSourceForScope(scopeType: UserPreferenceScopeType): UserPreferenceSource {
  switch (scopeType) {
    case "user":
      return "user";
    case "permissionTemplateGroup":
      return "role";
    case "location":
      return "location";
    case "client":
      return "client";
    case "global":
      return "global";
    default:
      return "default";
  }
}

export function collectionUrlForScope(params: UserPreferenceScopeParams): string | null {
  switch (params.scopeType) {
    case "global":
      return "/api/user_preferences/global";
    case "client":
      if (!params.scopeIri && !params.clientIri) {
        return null;
      }
      return `${params.scopeIri ?? params.clientIri}/user_preferences`;
    case "location":
      if (!params.scopeIri && !params.locationIri) {
        return null;
      }
      return `${params.scopeIri ?? params.locationIri}/user_preferences`;
    case "permissionTemplateGroup":
      if (!params.scopeIri) {
        return null;
      }
      return `${params.scopeIri}/user_preferences`;
    case "user":
      if (!params.userIri) {
        return null;
      }
      return `${params.userIri}/user_preferences`;
    default:
      return null;
  }
}

export function scopeRelationsForWrite(params: UserPreferenceScopeParams): Pick<UserPreferencesWrite, "relatedUser" | "client" | "location" | "permissionTemplateGroup"> {
  switch (params.scopeType) {
    case "client":
      return {
        client: params.clientIri ?? params.scopeIri ?? null,
      };
    case "location":
      return {
        client: params.clientIri ?? null,
        location: params.locationIri ?? params.scopeIri ?? null,
      };
    case "permissionTemplateGroup":
      return {
        client: params.clientIri ?? null,
        permissionTemplateGroup: params.scopeIri ?? null,
        ...(params.locationIri ? { location: params.locationIri } : {}),
      };
    case "user":
      return {
        relatedUser: params.userIri ?? params.scopeIri ?? null,
        ...(params.locationIri ? { location: params.locationIri } : {}),
      };
    case "global":
    default:
      return {};
  }
}

export function inheritedQueryParams(params: UserPreferenceScopeParams): Record<string, string> {
  const query: Record<string, string> = {};
  if (params.clientIri) {
    query.client = params.clientIri;
  }
  if (params.locationIri) {
    query.location = params.locationIri;
  }
  if (params.scopeType === "permissionTemplateGroup" && params.scopeIri) {
    query.permissionTemplateGroup = params.scopeIri;
  }
  if (params.scopeType === "user" && (params.userIri || params.scopeIri)) {
    query.relatedUser = params.userIri ?? params.scopeIri ?? "";
  }
  return query;
}

export function pickOwnRow(members: UserPreferencesHydraItem[], params: UserPreferenceScopeParams): UserPreferencesHydraItem | null {
  const locationIri = params.locationIri ?? null;
  const apiScope = scopeTypeToApiScope(params.scopeType);

  const scoped = members.filter((row) => {
    if (row.scope && row.scope !== apiScope) {
      return false;
    }
    if (params.scopeType === "user") {
      const userIri = params.userIri ?? params.scopeIri ?? null;
      if (userIri && row.relatedUser && !matchesIri(row.relatedUser, userIri)) {
        return false;
      }
    }
    if (params.scopeType === "client") {
      const clientIri = params.clientIri ?? params.scopeIri ?? null;
      if (clientIri && row.client && !matchesIri(row.client, clientIri)) {
        return false;
      }
      if (row.location) {
        return false;
      }
    }
    if (params.scopeType === "location") {
      const locationScopeIri = params.locationIri ?? params.scopeIri ?? null;
      if (locationScopeIri && !matchesIri(row.location, locationScopeIri)) {
        return false;
      }
    }
    if (params.scopeType === "permissionTemplateGroup") {
      if (params.scopeIri && row.permissionTemplateGroup && !matchesIri(row.permissionTemplateGroup, params.scopeIri)) {
        return false;
      }
    }
    if (params.scopeType === "global") {
      return !row.relatedUser && !row.client && !row.location && !row.permissionTemplateGroup;
    }
    return true;
  });

  if (locationIri && params.scopeType !== "location") {
    return scoped.find(row => matchesIri(row.location, locationIri)) ?? null;
  }

  if (params.scopeType === "user" || params.scopeType === "permissionTemplateGroup") {
    return scoped.find(row => !row.location) ?? null;
  }

  return scoped[0] ?? null;
}

export function unwrapPreferenceCollection(data: UserPreferencesHydraItem | HydraCollection<UserPreferencesHydraItem> | null | undefined): UserPreferencesHydraItem[] {
  if (!data) {
    return [];
  }
  if ("member" in data && Array.isArray(data.member)) {
    return data.member as UserPreferencesHydraItem[];
  }
  if ("id" in data) {
    return [data];
  }
  return [];
}

export function isPreferenceKeyOverridden(row: UserPreferencesHydraItem | null, key: UserPreferenceKey): boolean {
  if (!row) {
    return false;
  }
  const value = row[key];
  return value !== null && value !== undefined;
}
